import {ONE_HOUR_MS, ONE_WEEK_MS} from '../utils/time';

/**
 * Canonical UTC instant for a section slot.
 *
 * Sections are fixed UTC instants: the `(course.firstSectionTimestamp,
 * timeIndex, weekIndex)` triple deterministically maps to one UTC
 * millisecond, regardless of where the code runs or which student is
 * viewing the schedule. This helper is the single source of truth for
 * that mapping across the webapp and Cloud Functions; every other
 * "when does section X happen?" call should delegate here rather than
 * reimplement the arithmetic.
 *
 * Implementation is deliberately pure raw-ms arithmetic. No
 * `Date.prototype.setDate`, `setHours`, `setMonth`, `setFullYear`, or
 * any `get*` counterpart of those is permitted in section-time math.
 * Those methods silently take `process.env.TZ` as an implicit input —
 * browsers run in the user's local timezone, Cloud Functions run in
 * UTC — so using them makes the output of the same function
 * environment-dependent. PR #1975 (Mar 2026) introduced exactly that
 * divergence in `getSectionDateForWeek` via `setUTCDate` + `setHours`
 * and shipped a silent client/server mismatch that wasn't caught until
 * the Egypt DST regression: students at `timeIndex=23` (local 23:00)
 * saw Week 2 slip by a full day after their country's spring-forward
 * transition. The TypeScript signature gave no hint that tz was an
 * input. Do not reintroduce local-tz methods here.
 *
 * After a DST transition in a student's country, the instant returned
 * for a given week does not change. The local wall-clock rendering of
 * that instant will shift by the student's zone-offset delta — that is
 * the intended product behavior: the section was scheduled in UTC at
 * course start, and students agreed to that UTC instant. Rendering it
 * in a shifted local tz is a presentation concern, handled at
 * `toLocaleString` / `Intl.DateTimeFormat` boundaries only.
 *
 * @param firstSectionTimestamp - ISO 8601 string from
 *   `course.firstSectionTimestamp`. The course-wide anchor
 *   corresponding to `timeIndex: 0, weekIndex: 0`.
 * @param timeIndex - Hour offset from the anchor. May be negative for
 *   slots scheduled before the baseline section. No wraparound — the
 *   full signed-integer range is valid.
 * @param weekIndex - 0-based section-week index. 0 is course week 1.
 *   May be negative.
 * @returns A `Date` whose epoch-ms is
 *   `Date.parse(firstSectionTimestamp) + timeIndex * ONE_HOUR_MS +
 *   weekIndex * ONE_WEEK_MS`. Output is byte-identical across runtime
 *   timezones.
 * @throws if `firstSectionTimestamp` is not a parseable ISO string
 *   (i.e. `Date.parse` returns `NaN`).
 */
export function computeSectionInstant({
  firstSectionTimestamp,
  timeIndex,
  weekIndex,
}: {
  firstSectionTimestamp: string;
  timeIndex: number;
  weekIndex: number;
}): Date {
  const anchorMs = Date.parse(firstSectionTimestamp);
  if (Number.isNaN(anchorMs)) {
    throw new Error(
      `computeSectionInstant: unparseable firstSectionTimestamp "${firstSectionTimestamp}"`,
    );
  }
  return new Date(anchorMs + timeIndex * ONE_HOUR_MS + weekIndex * ONE_WEEK_MS);
}

/**
 * Finds the next upcoming section instant in the sequence defined by
 * `(firstSectionTimestamp, timeIndex)`. A section remains "current"
 * (not yet replaced by the next week's slot) for `graceMs` after its
 * start instant — defaulting to one hour, matching the student
 * dashboard's pre-existing grace window.
 *
 * Pure and deterministic: `nowMs` is an explicit parameter so callers
 * thread in either server time or a test fixture clock. No internal
 * `Date.now()`, no ambient-tz reads. Output instant is byte-identical
 * across runtime timezones, inheriting {@link computeSectionInstant}'s
 * sticky-UTC contract.
 *
 * @param firstSectionTimestamp - ISO 8601 course anchor (timeIndex 0,
 *   weekIndex 0).
 * @param timeIndex - Hour offset from the anchor.
 * @param nowMs - Current epoch-ms. Caller-provided.
 * @param graceMs - Window during which the just-started section is
 *   still considered "next". Defaults to `ONE_HOUR_MS`. Pass `0` to
 *   advance to the next week at the exact section start instant.
 * @returns The upcoming section instant and its 0-based section-week
 *   index. `sectionWeekIdx + 1` is the 1-based "course week" the UI
 *   typically shows to users.
 * @throws if `firstSectionTimestamp` is unparseable (propagated from
 *   {@link computeSectionInstant}).
 */
export function computeNextSectionSlot({
  firstSectionTimestamp,
  timeIndex,
  nowMs,
  graceMs = ONE_HOUR_MS,
}: {
  firstSectionTimestamp: string;
  timeIndex: number;
  nowMs: number;
  graceMs?: number;
}): {nextSectionDate: Date; sectionWeekIdx: number} {
  const firstMeetingDate = computeSectionInstant({
    firstSectionTimestamp,
    timeIndex,
    weekIndex: 0,
  });
  return computeNextWeeklyInstant({firstMeetingDate, nowMs, graceMs});
}

/** Applies weekly recurrence and the section-start grace period to one date. */
function computeNextWeeklyInstant({
  firstMeetingDate,
  nowMs,
  graceMs,
}: {
  firstMeetingDate: Date;
  nowMs: number;
  graceMs: number;
}): {nextSectionDate: Date; sectionWeekIdx: number} {
  const firstMeetingMs = firstMeetingDate.getTime();
  let sectionWeekIdx = 0;
  let sectionDate = new Date(firstMeetingMs);
  while (nowMs >= sectionDate.getTime() + graceMs) {
    sectionWeekIdx += 1;
    sectionDate = new Date(firstMeetingMs + sectionWeekIdx * ONE_WEEK_MS);
  }
  return {nextSectionDate: sectionDate, sectionWeekIdx};
}

/**
 * Returns the 0-based course section week containing a Super section's first
 * meeting. A missing or invalid course anchor treats that meeting as week 1.
 */
export function getSuperSectionCourseWeekIndex({
  superSectionFirstTimestamp,
  courseFirstSectionTimestamp,
}: {
  superSectionFirstTimestamp: string;
  courseFirstSectionTimestamp?: string;
}): number {
  const superAnchorMs = Date.parse(superSectionFirstTimestamp);
  if (!Number.isFinite(superAnchorMs)) {
    throw new Error(
      `getSuperSectionCourseWeekIndex: unparseable Super section timestamp "${superSectionFirstTimestamp}"`,
    );
  }

  const courseAnchorMs = courseFirstSectionTimestamp
    ? Date.parse(courseFirstSectionTimestamp)
    : Number.NaN;
  if (!Number.isFinite(courseAnchorMs)) return 0;

  return Math.max(
    0,
    Math.floor((superAnchorMs - courseAnchorMs) / ONE_WEEK_MS),
  );
}

/**
 * Finds the next meeting for a Super section and maps it to the course week
 * used by the section page and RSVP documents.
 *
 * @param superSectionFirstTimestamp - ISO 8601 timestamp of the first Super
 *   meeting. Its offset is part of the scheduled instant.
 * @param courseFirstSectionTimestamp - Optional course section anchor used to
 *   align the Super meeting with the course's section-week numbering. If it is
 *   omitted or invalid, the Super section's first meeting is treated as week 1.
 * @param nowMs - Current epoch milliseconds supplied by the caller.
 * @return The upcoming meeting and its zero-based course week index. Add 1 to
 *   `courseWeekIdx` to get the one-based RSVP document key.
 * @throws If `superSectionFirstTimestamp` is not a parseable ISO timestamp.
 */
export function computeNextSuperSectionSlot({
  superSectionFirstTimestamp,
  courseFirstSectionTimestamp,
  nowMs,
}: {
  superSectionFirstTimestamp: string;
  courseFirstSectionTimestamp?: string;
  nowMs: number;
}): {nextSectionDate: Date; courseWeekIdx: number} {
  const firstMeetingCourseWeekIdx = getSuperSectionCourseWeekIndex({
    superSectionFirstTimestamp,
    courseFirstSectionTimestamp,
  });
  const {nextSectionDate, sectionWeekIdx} = computeNextWeeklyInstant({
    firstMeetingDate: new Date(superSectionFirstTimestamp),
    nowMs,
    graceMs: ONE_HOUR_MS,
  });

  return {
    nextSectionDate,
    courseWeekIdx: firstMeetingCourseWeekIdx + sectionWeekIdx,
  };
}
